import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { jobOpenings, careerValues, heroImages } from '../data/mockData';
import { Briefcase, MapPin, Clock, ChevronDown, ChevronUp, Send, CheckCircle2 } from 'lucide-react';

export const Careers = () => {
  const { toast } = useToast();
  const [expandedJob, setExpandedJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    coverLetter: '',
    resume: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resume') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare form data for Web3Forms
      const web3formsData = {
        access_key: '9bac6cc6-ba63-4e18-840b-e6695727e296',
        subject: `Job Application - ${selectedJob.title}`,
        from_name: formData.name,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        linkedin: formData.linkedin,
        position: selectedJob.title,
        cover_letter: formData.coverLetter,
        message: `
JOB APPLICATION FROM WEBSITE

Position: ${selectedJob.title}
Applicant Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
LinkedIn: ${formData.linkedin}

Cover Letter:
${formData.coverLetter}
        `.trim()
      };

      // Submit to Web3Forms
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(web3formsData)
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Application Submitted Successfully!",
          description: `Thank you for applying to the ${selectedJob.title} position. We'll review your application and be in touch soon.`,
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          linkedin: '',
          coverLetter: '',
          resume: null
        });
        setSelectedJob(null);
      } else {
        throw new Error('Submission failed');
      }
      
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: "Please try again or email your application directly to careers@allpartsmanufacturing.com",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImages.factory} 
            alt="Careers" 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <Badge className="mb-4">We're Hiring</Badge>
          <h1 className="text-5xl font-bold text-foreground mb-6">Join Our Team</h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            Help Canadian companies bring their products to market through our global manufacturing network. Build your career with a growing team that values innovation, quality, and customer success.
          </p>
        </div>
      </section>

      {/* Why Work Here */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why All Parts Manufacturing?</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              More than a job—a career with global impact and growth potential
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {careerValues.map((value, index) => (
              <Card key={index} className="hover:shadow-lg transition-smooth">
                <CardContent className="p-6">
                  <div className="h-12 w-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Open Positions</h2>
            <p className="text-lg text-muted-foreground">
              {jobOpenings.length} opportunities to join our growing team
            </p>
          </div>

          <div className="space-y-6">
            {jobOpenings.map((job) => (
              <Card key={job.id} className="hover:shadow-lg transition-smooth">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-2xl font-bold text-foreground">{job.title}</h3>
                        <Badge variant="outline" className="ml-2">{job.type}</Badge>
                      </div>
                      <p className="text-muted-foreground mb-4">{job.description}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-accent" />
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Briefcase className="h-4 w-4 text-accent" />
                          <span>{job.department}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-accent" />
                          <span>{job.type}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Details */}
                  {expandedJob === job.id && (
                    <div className="mt-6 space-y-6 border-t pt-6">
                      <div>
                        <h4 className="font-bold mb-3">Key Responsibilities</h4>
                        <ul className="space-y-2">
                          {job.responsibilities.map((resp, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-sm text-muted-foreground">
                              <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                              <span>{resp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-bold mb-3">Qualifications</h4>
                        <ul className="space-y-2">
                          {job.qualifications.map((qual, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-sm text-muted-foreground">
                              <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                              <span>{qual}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-bold mb-3">Benefits & Perks</h4>
                        <ul className="space-y-2">
                          {job.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-sm text-muted-foreground">
                              <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                      className="group"
                    >
                      {expandedJob === job.id ? (
                        <>
                          Hide Details
                          <ChevronUp className="ml-2 h-4 w-4 group-hover:-translate-y-1 transition-transform" />
                        </>
                      ) : (
                        <>
                          View Details
                          <ChevronDown className="ml-2 h-4 w-4 group-hover:translate-y-1 transition-transform" />
                        </>
                      )}
                    </Button>
                    <Button
                      className="bg-accent hover:bg-accent/90"
                      onClick={() => {
                        setSelectedJob(job);
                        window.scrollTo({ top: document.getElementById('apply-form')?.offsetTop - 100, behavior: 'smooth' });
                      }}
                    >
                      Apply Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-3xl mx-auto">
          <Card>
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2">Apply for a Position</h2>
                {selectedJob ? (
                  <p className="text-muted-foreground">
                    Applying for: <span className="font-semibold text-accent">{selectedJob.title}</span>
                  </p>
                ) : (
                  <p className="text-muted-foreground">Select a position above or apply generally</p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {!selectedJob && (
                  <div>
                    <Label htmlFor="position">Position of Interest *</Label>
                    <select
                      id="position"
                      className="w-full mt-2 px-3 py-2 border border-input rounded-md bg-background"
                      required
                      onChange={(e) => {
                        const job = jobOpenings.find(j => j.id === parseInt(e.target.value));
                        setSelectedJob(job);
                      }}
                    >
                      <option value="">Select a position</option>
                      {jobOpenings.map((job) => (
                        <option key={job.id} value={job.id}>{job.title}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+1 (604) 555-0100"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="linkedin">LinkedIn Profile</Label>
                    <Input
                      id="linkedin"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleChange}
                      placeholder="linkedin.com/in/yourprofile"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="coverLetter">Cover Letter / Why You're Interested *</Label>
                  <Textarea
                    id="coverLetter"
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleChange}
                    required
                    placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                    rows={6}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="resume">Resume / CV * (PDF, DOC, DOCX)</Label>
                  <Input
                    id="resume"
                    name="resume"
                    type="file"
                    onChange={handleChange}
                    accept=".pdf,.doc,.docx"
                    required
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Maximum file size: 5MB
                  </p>
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-accent hover:bg-accent/90"
                    disabled={isSubmitting || !selectedJob}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    <Send className="ml-2 h-5 w-5" />
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground text-center">
                  Applications are sent to <strong>careers@allpartsmanufacturing.com</strong><br />
                  We review all applications and respond within 5-7 business days.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Don't See the Right Position?</h3>
          <p className="text-muted-foreground mb-6">
            We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <a href="mailto:careers@allpartsmanufacturing.com">
            <Button variant="outline" size="lg">
              Email Your Resume
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};
